import {PayloadAction, createSlice} from '@reduxjs/toolkit';
import {
    getSavedSearchesCountsFailure,
    getSavedSearchesCountsRequest,
    getSavedSearchesCountsSuccess,
} from 'src/openapi/admin/apis/UserContentCountApi';

type SearchesState = {
    total: number,
    status: 'idle' | 'loading' | 'succeeded' | 'failed',
    error?: string,
    receivedAt?: number,
};

export type UserPageState = {
    userPage: {
        tabs: {
            content: {
                searches: SearchesSliceState,
            },
        },
    },
};

type SearchesSliceState = {
    countsByUserId: Record<number, SearchesState>,
};

const initialState: SearchesSliceState = {
    countsByUserId: {},
};

const computeTotal = (data: Record<string, number | null | undefined>): number => {
    if (!data) {
        return 0;
    }

    const values = Object.values(data) as Array<number | null | undefined>;

    return values.reduce((sum: number, val) => sum + (val ?? 0), 0);
};

export const searchesSlice = createSlice({
    name: 'searches',
    initialState,
    reducers: {
        invalidateUserSearches: (state, action: PayloadAction<number>) => {
            const userId = action.payload;
            const userSearches = state.countsByUserId[userId];
            if (userSearches) {
                userSearches.status = 'idle';
                userSearches.receivedAt = undefined;
            }
        },
    },
    extraReducers: builder => {
        builder
            .addCase(getSavedSearchesCountsRequest, (state, action) => {
                const {userId} = action.payload.meta;
                if (!state.countsByUserId[userId]) {
                    state.countsByUserId[userId] = {
                        total: 0,
                        status: 'loading',
                    };
                } else {
                    state.countsByUserId[userId].status = 'loading';
                }
            })
            .addCase(getSavedSearchesCountsSuccess, (state, action) => {
                const {userId} = action.payload.meta;
                const data = action.payload.data;
                const total = computeTotal(data);

                state.countsByUserId[userId] = {
                    total,
                    status: 'succeeded',
                    receivedAt: Date.now(),
                };
            })
            .addCase(getSavedSearchesCountsFailure, (state, action) => {
                const {userId} = action.payload.meta;
                state.countsByUserId[userId] = {
                    total: 0,
                    status: 'failed',
                    error: action.payload.data?.message ?? 'Unknown error',
                };
            });
    },
});

export const {invalidateUserSearches} = searchesSlice.actions;
